import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ReactFlowProvider} from 'reactflow';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {SchemaInfo} from 'processes/modals/field-mapping/FieldMappingConstants';
import {TooltipWrapper, TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {ProcessFlowchartConfigVersionForEditorGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigVersionForEditorGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ClientSchemaMapperConfig, isPlaceholderTaskSchema} from '../FlowchartConfigConstants';
import SchemaMapperConfigsPane from '../SchemaMapperConfigsPane';
import buildStaticDataFromFlowchartConfigGraphQL from './buildStaticDataFromFlowchartConfigGraphQL';
import {
  ProcessFlowchartConfigWithValidSchemaGQL,
  CustomMappingData,
  isCustomMappingDataEmpty,
  StaticTemplateFlowchart,
} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorContent from './ProcessFlowchartConfigEditorContent';
import {getAvailableTaskSchemaInfos} from './processFlowchartConfigEditorUtils';
import {useAllSchemasById} from './useAllSchemasById';

export enum FlowchartConfigTab {
  SCHEMA_MAPPER_CONFIGS = 'SCHEMA_MAPPER_CONFIGS',
  TEMPLATE_FLOWCHART = 'TEMPLATE_FLOWCHART',
}

interface ProcessFlowchartConfigEditorHeaderProps {
  locked: boolean;
  submitDisabledMessage: string | undefined;
  onCancel: () => void;
  onSubmit: () => void;
}

function ProcessFlowchartConfigEditorHeader(props: ProcessFlowchartConfigEditorHeaderProps): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-header', css(styles.selectItemContainer))}>
      <div className="simpleDivTable-headerCell simpleDivTable-header--title">Flowchart configuration</div>
      <div className={css(styles.selectItemContainer)}>
        {!props.locked && (
          <>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MINI}
              onClick={props.onCancel}
              text="Cancel"
              className={css(styles.buttonContainer)}
              dataTestAttrs={{
                component: 'GraphBuilderFlowchartConfigHeader',
                element: 'cancelButton',
              }}
            />
            <TooltipWrapper
              tooltipTitle={props.submitDisabledMessage}
              tooltipPlacement={TooltipPlacement.AUTO}
            >
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                size={ButtonSize.MINI}
                onClick={props.onSubmit}
                text="Submit"
                disabled={props.submitDisabledMessage != null}
                className={css(styles.buttonContainer)}
                dataTestAttrs={{
                  component: 'GraphBuilderFlowchartConfigHeader',
                  element: 'submitButton',
                }}
              />
            </TooltipWrapper>
          </>
        )}
      </div>
    </div>
  );
}

interface ProcessFlowchartConfigEditorControllerProps {
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  flowchartConfig: ProcessFlowchartConfigWithValidSchemaGQL;
  flowchartConfigVersion: ProcessFlowchartConfigVersionForEditorGraphQL;
  locked: boolean;
  onChangeFlowchart?: () => void;
}

/**
 * Process FlowchartConfig editor that displays the specified flowchart config version.
 *
 * Ensures queries have completed before rendering the editor.
 */
export default function ProcessFlowchartConfigEditorController(
  props: ProcessFlowchartConfigEditorControllerProps
): JSX.Element {
  const rootTaskSchema = useMemo(() => props.flowchartConfig.schema, [props.flowchartConfig.schema]);

  const {schemasById: allSchemasById, processTaskSchemas: allTaskSchemas} = useAllSchemasById(rootTaskSchema);

  if (allTaskSchemas == null || allSchemasById == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <InnerProcessFlowchartConfigEditorController
      {...props}
      rootTaskSchema={rootTaskSchema}
      allTaskSchemas={allTaskSchemas}
      allSchemasById={allSchemasById}
      onChangeFlowchart={props.onChangeFlowchart}
    />
  );
}

interface InnerProcessFlowchartConfigEditorControllerProps {
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  flowchartConfig: ProcessFlowchartConfigWithValidSchemaGQL;
  flowchartConfigVersion: ProcessFlowchartConfigVersionForEditorGraphQL;
  locked: boolean;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  allTaskSchemas: Array<ProcessTaskSchemaGraphQL>;
  allSchemasById: SchemaById;
  onChangeFlowchart?: () => void;
}

/**
 * Converts GraphQL flowchart shape to a static representation of nodes and edges to pass to the flowchart editor.
 */
function InnerProcessFlowchartConfigEditorController(
  props: InnerProcessFlowchartConfigEditorControllerProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<FlowchartConfigTab>(FlowchartConfigTab.TEMPLATE_FLOWCHART);

  const {
    nodesStaticData,
    edgesStaticData,
    schemaMapperConfigs: initialSchemaMapperConfigs,
    availableTaskSchemas,
    customMappingDataByEdgeId: initialCustomMappingDataByEdgeId,
  } = useMemo(
    () =>
      buildStaticDataFromFlowchartConfigGraphQL({
        flowchartConfigVersion: props.flowchartConfigVersion,
        rootTaskSchema: props.rootTaskSchema,
        allTaskSchemas: props.allTaskSchemas,
      }),
    [props.flowchartConfigVersion, props.allTaskSchemas, props.rootTaskSchema]
  );

  const [schemaMapperConfigs, setSchemaMapperConfigs] = useState<Array<ClientSchemaMapperConfig>>(
    initialSchemaMapperConfigs ?? []
  );

  const [customMappingDataByEdgeId, setCustomMappingDataByEdgeId] = useState<
    Record<string, CustomMappingData>
  >(initialCustomMappingDataByEdgeId);

  const allowedNodeExecutionTypes = useMemo(
    () => props.flowchartEditorConfigurator.allowedNodeExecutionTypes,
    [props.flowchartEditorConfigurator]
  );

  const [availableTaskSchemaInfosForSidepanel, setAvailableTaskSchemaInfosForSidepanel] = useState<
    Array<SchemaInfo>
  >(
    getAvailableTaskSchemaInfos(
      props.allTaskSchemas,
      nodesStaticData,
      props.rootTaskSchema,
      allowedNodeExecutionTypes
    )
  );

  useEffect(() => {
    setAvailableTaskSchemaInfosForSidepanel(
      getAvailableTaskSchemaInfos(
        props.allTaskSchemas,
        nodesStaticData,
        props.rootTaskSchema,
        allowedNodeExecutionTypes
      )
    );
  }, [nodesStaticData, props.allTaskSchemas, props.rootTaskSchema, allowedNodeExecutionTypes]);

  const [staticFlowchartData, setStaticFlowchartData] = useState(() => ({
    nodesStaticData,
    edgesStaticData,
  }));

  const handleSave = useCallback((newStaticFlowchartData: StaticTemplateFlowchart) => {
    // TODO(BNCH-128398): Implement save with DAO call
  }, []);

  // Save the flowchart data immediately, without waiting for an auto save
  // TODO: Implement auto save
  const handleSaveNow = useCallback(
    (newStaticFlowchartData: StaticTemplateFlowchart) => {
      handleSave(newStaticFlowchartData);
    },
    [handleSave]
  );

  const handleFlowchartUpdate = useCallback((newStaticFlowchartData: StaticTemplateFlowchart) => {
    setStaticFlowchartData(newStaticFlowchartData);
  }, []);

  // TODO: Temporary header and tabs, will evolve
  return (
    <div className={css(styles.editorContainer)}>
      <ProcessFlowchartConfigEditorHeader
        locked={props.locked}
        submitDisabledMessage={undefined}
        onCancel={noop}
        onSubmit={() => {
          handleSave(staticFlowchartData);
        }}
      />
      <div className={css(styles.divider)} />
      <TabContainer<FlowchartConfigTab>
        activeTab={activeTab}
        onRequestTabChange={(tab) => {
          setActiveTab(tab);
        }}
        className={classNames('u-flex-grow', css(styles.tabContainer))}
      >
        <Tab tabKey={FlowchartConfigTab.TEMPLATE_FLOWCHART} title="Template Flowchart">
          <ReactFlowProvider>
            <ProcessFlowchartConfigEditorContent
              flowchart={staticFlowchartData}
              flowchartEditorConfigurator={props.flowchartEditorConfigurator}
              allSchemasById={props.allSchemasById}
              rootTaskSchema={props.rootTaskSchema}
              availableTaskSchemaInfosForSidepanel={availableTaskSchemaInfosForSidepanel}
              customMappingDataByEdgeId={customMappingDataByEdgeId}
              onChangeCustomMappingData={(edgeId, newCustomMappingData) => {
                if (isCustomMappingDataEmpty(newCustomMappingData)) {
                  setCustomMappingDataByEdgeId((prev) => {
                    const newCustomMappingDataByEdgeId = {...prev};
                    delete newCustomMappingDataByEdgeId[edgeId];
                    return newCustomMappingDataByEdgeId;
                  });
                } else {
                  setCustomMappingDataByEdgeId((prev) => ({...prev, [edgeId]: newCustomMappingData}));
                }
              }}
              onChangeStaticFlowchartData={handleFlowchartUpdate}
              onChangeFlowchart={props.onChangeFlowchart}
              onSaveNow={handleSaveNow}
              locked={props.locked}
            />
          </ReactFlowProvider>
        </Tab>
        {props.flowchartEditorConfigurator.hasFeature(
          ProcessFlowchartPlatformFeatureType.ALLOW_COMPLEX_TRANSITIONS
        ) && (
          <Tab tabKey={FlowchartConfigTab.SCHEMA_MAPPER_CONFIGS} title="Transitions">
            {/* TODO: Temporarily solves ACM/PCM transition needs */}
            <SchemaMapperConfigsPane
              isEditing={!props.locked}
              schemaMapperConfigs={schemaMapperConfigs}
              availableTaskSchemas={
                availableTaskSchemas.filter(
                  (availableTaskSchema) => !isPlaceholderTaskSchema(availableTaskSchema)
                ) as Array<ProcessTaskSchemaGraphQL>
              }
              allSchemasById={props.allSchemasById}
              allTaskSchemas={props.allTaskSchemas}
              rootTaskSchema={props.rootTaskSchema}
              onChange={setSchemaMapperConfigs}
            />
          </Tab>
        )}
      </TabContainer>
    </div>
  );
}

const styles = StyleSheet.create({
  editorContainer: {
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    position: 'relative',
  },
  divider: {
    height: '1px',
    width: '100%',
    marginTop: '30px',
    left: '0px',
    background: `${Color.GRAY_200}`,
    position: 'absolute',
  },
  tabContainer: {
    padding: '8px 14px',
  },
  selectItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  buttonContainer: {
    marginLeft: '15px',
  },
  bannerWrapper: {
    margin: '16px',
  },
  tabIcon: {
    color: Color.RED_600,
    marginRight: '2px',
    transform: 'scale(1.2)',
  },
});
